/**
 * The colours a waiter can be given on a shared ticket.
 *
 * Hand-picked rather than generated: they have to stay apart from each other at
 * dot size, and clear of the colours the screen already spends, amber for an
 * open table, green for saved, red for gone.
 *
 * Spread around the wheel rather than clustered. An earlier set put magenta at
 * 320 next to pink at 340 and teal at 175 next to cyan at 200; twenty degrees
 * apart is a difference on a swatch and none at all on an 8px dot seen from
 * across a counter. These are at least fifty apart, and the lightness moves
 * with the hue so two of them never arrive as the same weight of colour.
 */
const PALETTE = [
  'oklch(0.58 0.20 258)', // blue
  'oklch(0.62 0.16 160)', // teal
  'oklch(0.60 0.21 350)', // pink
  'oklch(0.68 0.16 80)', // ochre
  'oklch(0.55 0.20 300)', // violet
  'oklch(0.50 0.10 200)', // slate
]

/**
 * Hands out colours to the people on one ticket, taking them in turn.
 *
 * Deriving each colour from its own name looked right until two names landed
 * on neighbouring entries, violet and magenta are thirty degrees apart, which
 * is a difference on a swatch and none at all on an 8px dot. Walking the list
 * in order guarantees the first two are as far apart as the palette allows,
 * which is the case that actually happens: a table is served by two people,
 * rarely by five.
 *
 * Order comes from the ticket, so the same person keeps the same dot for as
 * long as that ticket is open.
 */
export function waiterColors(names: string[]): Map<string, string> {
  const map = new Map<string, string>()
  let i = 0
  for (const name of names) {
    if (!map.has(name)) map.set(name, PALETTE[i++ % PALETTE.length])
  }
  return map
}

/**
 * Picks a stable colour for a name.
 *
 * Derived from the name rather than from position in a list: a waiter keeps the
 * same dot on every ticket and on every device, so "the blue ones are mine"
 * holds all service. Two names can collide on a colour, with six in the
 * palette that needs seven waiters on one table, and the name is written out
 * beside the dots anyway.
 */
export function waiterColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  return PALETTE[hash % PALETTE.length]
}
